<template>
  <div>
    <div v-if="banner && banner.image_url" class="banner-over-box">
      <div class="banner-box">
        <common-banner :banner="banner" :is-fixed-dimension="true" />
      </div>
    </div>
    <div>
      <div class="container">
        <div class="w-full">
          <h1 class="headingRanking">
            {{ commonHeadingLargeTitle }}
            <span class="rankingDate">{{ targetDateOnFormat }}</span>
          </h1>
          <p v-if="areaName">
            <custom-nuxt-link v-if="prefecture" :to="linkToPrefectureRankingPath" class="navLinkList-item linkToPrefecture">
              {{ prefecture.name }}のランキングはこちら
            </custom-nuxt-link>
          </p>
        </div>
      </div>
      <div class="container">
        <div class="w-full">
          <div class="tabListWrapper">
            <ul class="tabList">
              <li :class="{ active: !['konkatsu', 'party', 'interest'].includes(eventType) }" class="commonButton btn-select-42">
                <custom-nuxt-link :to="`/area_rankings/${areaSlug}`" class="tabList-item">すべて</custom-nuxt-link>
              </li>
              <li :class="{ active: eventType === 'party' }" class="commonButton btn-select-42">
                <custom-nuxt-link :to="`/area_rankings/${areaSlug}/categories/party/`" class="tabList-item">恋活</custom-nuxt-link>
              </li>
              <li :class="{ active: eventType === 'konkatsu' }" class="commonButton btn-select-42">
                <custom-nuxt-link :to="`/area_rankings/${areaSlug}/categories/konkatsu/`" class="tabList-item">婚活</custom-nuxt-link>
              </li>
              <li :class="{ active: eventType === 'interest' }" class="commonButton btn-select-42">
                <custom-nuxt-link :to="`/area_rankings/${areaSlug}/categories/interest/`" class="tabList-item">趣味<wbr />コン</custom-nuxt-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="w-full">
        <div v-if="rankingEvents.length">
          <ranking-event-list :ranking-events="rankingEvents" />
        </div>
        <flash-message v-else type="error" message="該当エリアのランキングがありません。" class="marginY3" />
        <div class="mvl">
          <p v-if="eventType" class="mvl marginX3">
            <custom-nuxt-link :to="linkToEventListPath" class="commonButton btn-secondary-40">
              {{ areaName }}の{{ eventTypeText }}はこちら
            </custom-nuxt-link>
          </p>
          <p v-else class="mvl marginX3">
            <custom-nuxt-link :to="linkToEventListPath" class="commonButton btn-secondary-40"> {{ areaName }}の全イベントはこちら </custom-nuxt-link>
          </p>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="grayBg w-full">
        <breadcrumbs
          v-if="breadcrumb"
          :page="breadcrumb.page"
          :options="breadcrumb.options"
          display-mode="footer"
          class="opixFooterBreadCrumbs breadcrumbContent"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { findAreaBySlug } from '@/models/areas'
import { useMainStore } from '@/store'
import { timeConverter } from '@/utils/converter'
import { buildQueryString } from '@/utils/url-action'
import { generateCommonHeadingLargeTitle } from '@/utils/commonHeadingLargeTitle'
const Breadcrumbs = defineAsyncComponent(() => import('@/components/Breadcrumbs.vue'))
const CommonBanner = defineAsyncComponent(() => import('@/components/CommonBanner.vue'))
const FlashMessage = defineAsyncComponent(() => import('@/components/FlashMessage.vue'))
const RankingEventList = defineAsyncComponent(() => import('@/components/event/RankingEventList.vue'))
export default {
  components: { Breadcrumbs, FlashMessage, RankingEventList, CommonBanner },
  setup() {
    definePageMeta({
      middleware: ['validate-area-page']
    })
    const store = useMainStore()
    const route = useRoute()
    const area = findAreaBySlug(route.params.area)
    useHeadParams({
      $route: route,
      areaName: area ? area.name : ''
    })
    const query = { ...route.query }
    if (route.params.category) query.event_type = route.params.category
    const apiUrl = `ranking/areas/${route.params.area}/${buildQueryString(query)}`

    return store
      .dispatch('apiGet', apiUrl)
      .then(response => {
        return {
          rankingEvents: response.data.ranking,
          targetDateOn: response.data.target_date_on,
          prefecture: response.data.prefecture,
          areaGroup: response.data.area_group,
          banner: response.data.banner
        }
      })
      .catch(e => {
        const errorCode = parseInt(e.response && e.response.status)
        if (errorCode === 404) return { rankingEvents: [], targetDateOn: null, prefecture: null }
        const app = useNuxtApp()
        app.$error(errorCode)
      })
  },
  computed: {
    targetDateOnFormat() {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const rankingDate = this.targetDateOn || yesterday
      return timeConverter(rankingDate).fullDateStandard + '更新'
    },
    eventType() {
      return this.$route.params.category || ''
    },
    areaSlug() {
      return this.$route.params.area
    },
    areaName() {
      const area = findAreaBySlug(this.areaSlug)
      return area ? area.name : ''
    },
    eventTypeText() {
      return this.$useI18(`event.attributes.event_type.${this.eventType}`)
    },
    commonHeadingLargeTitle() {
      return generateCommonHeadingLargeTitle(this.areaName, this.eventTypeText)
    },
    descriptionText() {
      const type = this.eventType ? this.eventTypeText : '街コン・趣味コンなど'
      return `${this.areaName}の${type}から昨日人気だったイベントをランキング！人気すぎて売り切れてしまっていたらゴメンナサイ！`
    },
    linkToPrefectureRankingPath() {
      if (!this.prefecture) return ''
      if (this.eventType) return `/prefecture_rankings/${this.prefecture.slug}/categories/konkatsu/`
      return `/prefecture_rankings/${this.prefecture.slug}/`
    },
    linkToEventListPath() {
      const basePath = `/areas/${this.prefecture.slug}/${this.areaGroup.slug}/${this.areaSlug}`
      if (this.eventType === 'interest') return `${basePath}/interests/`
      if (this.eventType) return `${basePath}/categories/${this.eventType}/`
      return `${basePath}/`
    },
    breadcrumb() {
      return {
        page: 'areaRanking',
        options: {
          areaSlug: this.areaSlug,
          eventType: this.eventType,
          showRankingText: true
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.headingRanking {
  padding-left: 1.6rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  font-size: 2.4rem;
  font-weight: bold;
  .rankingDate {
    float: right;
    padding: 1rem;
    color: $gray-55;
    font-size: 12px;
    font-weight: normal;
  }
}

.banner-over-box {
  @include pc {
    background-color: $gray-55;
    padding: 2rem 0;
  }
}

.banner-box {
  width: 100%;
  padding: 0 1.6rem;
  @include pc {
    width: 47rem;
    margin: 0 auto;
  }
}

.linkToPrefecture {
  text-align: right;
  margin: 1.5rem 1rem;
  display: block;
}

.navLinkList-item::before {
  content: '';
  display: inline-block;
  border: 4px solid;
  border-right: 0;
  border-top-color: transparent;
  border-bottom-color: transparent;
}

.mvl {
  margin-top: 1.5rem;
}

.commonButton {
  width: 47rem;
  margin: auto;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;
}

.w-full {
  width: 100%;
}

.tabListWrapper {
  margin: 1rem 0;
  position: sticky;
  top: $pcHeaderHeight;
  z-index: 2;
  @include sp {
    top: $spHeaderHeight;
  }
  .tabList {
    width: inherit;
    padding: 0;
    border-bottom: 2px solid $cerulean-blue;
    &-item {
      margin: unset;
      color: $black-1a;
      word-break: keep-all;
    }
  }
}

li.active .tabList-item {
  color: $white !important;
}

.grayBg {
  background: $gray-f1;
}

.breadcrumbContent {
  .breadcrumbList {
    box-shadow: 0 0 3px $gray-c6;
    border-radius: 3px;
  }
}
</style>
